import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { hasAgentAssistAccess } from '@/reducers/auth/currentUser.redux'

const RtcSocketProvider = () => {
  const { isOpen: rtcSocketIsOpen } = useSelector((state) => state.realtimeSocket)
  const { username, token } = useSelector((state) => state.currentUser)
  const currentOrganization = useSelector((state) => state.currentOrganization)
  const dispatch = useDispatch()
  // The socket carries the live alerts AND the manager → agent chat, so it
  // opens for anyone who can use Agent Assist. Gating it on the per-user RTC
  // permission left managers who were sent a raised-hand notification with a
  // Chat button and no transport to send on (QCER-1103).
  //
  // This is now every logged-in user outside an audio-ingestion-only org. The
  // fan-out is the reason chat wants to move off this socket entirely and onto
  // the notifications socket, which is already open for everyone with no
  // entitlement coupling — tracked as a follow-up, not done here.
  const canUseAgentAssist = hasAgentAssistAccess(currentOrganization)
  // "Logged-in" has to be checked here, not assumed. This provider mounts above
  // the Router, so its first effect runs before ProtectedRoute has hydrated
  // currentUser, and rtcSocketMiddleware reads username and token off the store
  // at dispatch time. Opening on that first render sent the shared worker out as
  // `?username=null`; realtime-coaching closes that with a non-reconnecting
  // code and nothing re-opens it, so the manager never counted as present and
  // the offline-RTA evaluator wrote every alert row, deduped per call
  // (RTG3-1263). The credentials are part of the dependency so the open waits
  // for login and the socket follows a logout/login cycle.
  const shouldConnect = canUseAgentAssist && Boolean(username) && Boolean(token)

  // Reconnect belongs to the shared worker, so the open side deliberately does
  // not watch rtcSocketIsOpen (#11356): re-dispatching on every transient close
  // would race the worker's own backoff reconnect.
  useEffect(() => {
    if (shouldConnect && !rtcSocketIsOpen) {
      dispatch({ type: 'OPEN_RTM_SOCKET' })
    }
  }, [shouldConnect])

  // The close side does watch it. The org can finish loading as
  // audio-ingestion-only after the open was dispatched but before the worker
  // reports the socket open; keyed on shouldConnect alone, that close is missed
  // and the excluded org keeps an authenticated socket for the session.
  useEffect(() => {
    if (!shouldConnect && rtcSocketIsOpen) {
      dispatch({ type: 'CLOSE_RTM_SOCKET' })
    }
  }, [shouldConnect, rtcSocketIsOpen])

  return null
}

export default RtcSocketProvider
